import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'

function UserSelect({ value, onChange }) {
  const selectRef = useRef(null)
  const tsRef = useRef(null)

  useEffect(() => {
    if (!selectRef.current || !window.TomSelect) return
    if (tsRef.current) return

    tsRef.current = new window.TomSelect(selectRef.current, {
      placeholder: 'Search for a user...',
      allowEmptyOption: true,
      valueField: 'id',
      labelField: 'name_and_email',
      searchField: 'name_and_email',
      load: (query, callback) => {
        const encoded = encodeURIComponent(query)
        fetch(`/users?query=${encoded}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
          .then(response => response.json())
          .then(users => callback(users))
          .catch(() => callback())
      }
    })

    tsRef.current.on('change', (val) => {
      onChange(val)
    })

    return () => {
      if (tsRef.current) {
        tsRef.current.destroy()
        tsRef.current = null
      }
    }
  }, [])

  // Sync value
  useEffect(() => {
    if (tsRef.current && tsRef.current.getValue() !== value) {
      tsRef.current.setValue(value, true)
    }
  }, [value])

  return (
    <select ref={selectRef} defaultValue={value} />
  )
}

function CollaboratorModal({ onClose }) {
  const { selectedBoardId, canManage } = useBoardsContext()
  const { createCollaborator, updateCollaborator, deleteCollaborator } = useBoardMutations(selectedBoardId)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [permissionLevel, setPermissionLevel] = useState('view')

  const collaboratorsQuery = useQuery({
    queryKey: ['kanbanBoardCollaborators', selectedBoardId],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_boards/${selectedBoardId}/kanban_board_collaborators`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    },
    enabled: !!selectedBoardId
  })

  const collaborators = collaboratorsQuery.data?.collaborators || []

  const handleAdd = async () => {
    if (!selectedUserId) return
    await createCollaborator.mutateAsync({
      user_id: Number(selectedUserId),
      permission_level: permissionLevel
    })
    setSelectedUserId('')
    await collaboratorsQuery.refetch()
  }

  const handleUpdatePermission = async (id, newLevel) => {
    await updateCollaborator.mutateAsync({ id, permission_level: newLevel })
    await collaboratorsQuery.refetch()
  }

  const handleRemove = async (id) => {
    await deleteCollaborator.mutateAsync(id)
    await collaboratorsQuery.refetch()
  }

  return (
    <div className="kb-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Collaborators</h3>
          <button className="text-gray-400 hover:text-gray-600 text-lg" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="p-4">
          <div className="space-y-2 mb-4">
            {collaborators.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-2">No collaborators yet</div>
            )}
            {collaborators.map(collab => (
              <div key={collab.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                <div>
                  <div className="text-sm font-medium text-gray-700">{collab.user_name}</div>
                  <div className="text-xs text-gray-400">{collab.user_email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {canManage ? (
                    <>
                      <select
                        className="form-select text-xs rounded border-gray-300"
                        value={collab.permission_level}
                        onChange={(e) => handleUpdatePermission(collab.id, e.target.value)}
                      >
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                        <option value="full_control">Full Control</option>
                      </select>
                      <button
                        className="text-red-400 hover:text-red-600 text-xs"
                        onClick={() => handleRemove(collab.id)}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500 capitalize">{collab.permission_level}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {canManage && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-xs font-medium text-gray-500 uppercase mb-2">Add Collaborator</div>
              <div className="mb-2">
                <UserSelect
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                />
              </div>
              <div className="flex gap-2">
                <select
                  className="form-select text-sm rounded border-gray-300 flex-1"
                  value={permissionLevel}
                  onChange={(e) => setPermissionLevel(e.target.value)}
                >
                  <option value="view">View</option>
                  <option value="edit">Edit</option>
                  <option value="full_control">Full Control</option>
                </select>
                <button
                  className="inline-flex justify-center rounded-md px-4 py-2 bg-blue-100 text-sm leading-5 font-semibold text-blue-700 hover:bg-blue-50 active:bg-blue-200 cursor-pointer"
                  onClick={handleAdd}
                  disabled={!selectedUserId || createCollaborator.isPending}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button className="inline-flex justify-center rounded-md px-4 py-2 bg-gray-100 text-sm leading-5 font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-200 cursor-pointer" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

CollaboratorModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

export default CollaboratorModal
